import { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/apiService';
import { useCurrentUser } from './useCurrentUser';

const LOCAL_STORAGE_KEY = 'teams_data';
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes cache duration

// During the national-team competition (id 2327) club teams aren't relevant, so
// only national teams are cached. import.meta.env values are strings, hence the
// string comparison.
const NATIONAL_TEAMS_ONLY_COMPETITION_ID = '2327';
const filterTeamsForCompetition = (teams) =>
    import.meta.env.VITE_CURRENT_COMPETITION_ID === NATIONAL_TEAMS_ONLY_COMPETITION_ID
        ? teams.filter(team => team.is_national_team || team.name === 'Algeria')
        : teams;

// Module-level state to share across all hook instances
let listeners = [];
let isFetching = false;
let hasFetchedOnce = false;


// Helper functions for local storage
const getFromLocalStorage = () => {
    try {
        const cached = localStorage.getItem(LOCAL_STORAGE_KEY);
        if (!cached) return null;

        const { data, timestamp } = JSON.parse(cached);
        const now = Date.now();

        // Check if cache is still valid
        if (now - timestamp > CACHE_DURATION) {
            localStorage.removeItem(LOCAL_STORAGE_KEY);
            return null;
        }

        return data;
    } catch {
        return null;
    }
};

const saveToLocalStorage = (data) => {
    try {
        localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify({
            data,
            timestamp: Date.now()
        }));
    } catch (error) {
        console.error('Failed to save to local storage:', error);
    }
};

const fetchTeams = (setAreTeamsLoading) => {
    isFetching = true;
    hasFetchedOnce = true;
    if (setAreTeamsLoading) setAreTeamsLoading(true);

    apiService.apiGet(import.meta.env.VITE_API_URL + '/teams')
        .then((data) => {
            const teams = filterTeamsForCompetition(data.data || []);

            saveToLocalStorage(teams);

            // Update all listeners
            listeners.forEach(listener => listener(teams));
            isFetching = false;
            if (setAreTeamsLoading) setAreTeamsLoading(false);
        })
        .catch(() => {
            isFetching = false;
            if (setAreTeamsLoading) setAreTeamsLoading(false);
        });
};

// Callable from outside React (e.g. after login) to populate the cache eagerly
export const prefetchTeamsCache = () => {
    if (isFetching) return;
    fetchTeams();
};

export const useTeamsCache = () => {
    const [teams, setTeams] = useState(() => getFromLocalStorage());
    const [areTeamsLoading, setAreTeamsLoading] = useState(false);
    const [currentUser] = useCurrentUser();

    useEffect(() => {
        // Add this component to listeners
        listeners.push(setTeams);

        const cachedData = getFromLocalStorage();

        // Show cached value immediately (if any) to avoid a flash of empty state
        if (cachedData !== null) {
            setTeams(cachedData);
        }

        // Revalidate once per page load: fetch fresh data in the background so the
        // cache is refreshed whenever the page is (re)loaded. hasFetchedOnce is
        // module-level state that resets on a full page load, so this fires once
        // per load rather than on every component mount/navigation.
        // No currentUser guard: /teams-listing is public, and gating the fetch on a logged-in
        // user leaves logged-out visitors (and crawlers) looking at an empty list forever.
        if (!hasFetchedOnce && !isFetching) {
            fetchTeams(setAreTeamsLoading);
        }

        // Cleanup: remove listener when component unmounts
        return () => {
            listeners = listeners.filter(listener => listener !== setTeams);
        };
    }, [currentUser]);

    const refreshTeams = useCallback(() => {
        fetchTeams(setAreTeamsLoading);
    }, []);

    const ensureTeamsLoaded = useCallback(() => {
        // Check if we have valid cached data
        const cachedData = getFromLocalStorage();

        // Only fetch if no cached data and not already fetching
        if (cachedData === null && !isFetching) {
            fetchTeams(setAreTeamsLoading);
        }
    }, []);

    const clearCache = () => {
        localStorage.removeItem(LOCAL_STORAGE_KEY);
        setTeams(null);
        listeners.forEach(listener => listener(null));
        hasFetchedOnce = false;
    };

    return {
        teams,
        areTeamsLoading,
        clearCache,
        refreshTeams,
        ensureTeamsLoaded
    };
};
